import React, { useContext, useEffect, useState } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { useFormik } from 'formikV2'
import { compose } from 'recompose'

import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import platform from 'components/Trackers/platform'
import { MutationUpdateRoundUpAccountArgs, PageName, RoundUpAccount } from 'generated/types'
import { HeroText } from 'pages/InvestorQuestions/styled-components'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { IErrorModalProps, withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import {
  FormValues,
  handleSubmit as handleFormSubmit,
  initialValues,
  validationSchema,
} from './form'
import updateRoundupsProfileMutation from './gql/mutation.updateRoundupProfile.gql'
import {
  ActionWrapper,
  Container,
  FormRow,
  RoundUpSelectionList,
  StyledBody,
  StyledButton,
  StyledFormContainer,
  StyledLayout,
} from './styled-components'

type UpdateRoundUpProfileInput = MutationUpdateRoundUpAccountArgs

type UpdateRoundUpProfileResult = {
  roundUpAccount: RoundUpAccount
}

type Props = {
  header: string
  text: string
} & HeroTextProps &
  WithTieredRegistrationProps &
  WithFlowRouterProps &
  IErrorModalProps

const RoundUpsSetupScreenViewedEvent = {
  feature: 'Invest Onboarding Round Ups Automatic Vs Manual',
  event_description: 'Screen Viewed',
  platform,
  object: 'Screen',
  action: 'Viewed',
  object_name: 'investOnboardingRoundUpsAutomaticVsManual',
  screen: 'investOnboardingRoundUpsAutomaticVsManual',
  screen_name: 'investOnboardingRoundUpsAutomaticVsManual',
}

const NextButtonTappedEvent = {
  feature: 'Registration: Round-Ups Setup',
  event_description: 'Next button tapped',
  platform,
  object: 'Button',
  action: 'Tapped',
  object_name: 'registrationRoundUpsSetup',
  screen: 'registrationRoundUpsSetup',
  screen_name: 'registrationRoundUpsSetup',
}

const trackRoundupsSetupButtonTapped = (roundUpsEnabled: boolean) => {
  trackEvent('Button Tapped', {
    eventData: { ...NextButtonTappedEvent, roundUpsEnabled },
    flow: getFlow(),
  })
}

const enhance = compose<Props, {}>(
  withApolloErrorModal,
  withHeroText,
  withFlowRouter,
  withTieredRegistration,
)

const RoundupsSetup: React.FunctionComponent<Props> = ({
  closeErrorModal,
  route,
  showErrorModal,
}) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [updateRoundupsProfile] = useMutation<
    UpdateRoundUpProfileResult,
    UpdateRoundUpProfileInput
  >(updateRoundupsProfileMutation)
  const [roundupsEnabled, setRoundupsEnabled] = useState(true)

  const { getRemoteString } = useRemoteStrings()

  const { handleSubmit, setFieldValue, values } = useFormik<FormValues>({
    onSubmit: handleFormSubmit({
      closeErrorModal,
      route,
      showErrorModal,
      trackRoundupsSetupButtonTapped,
      updateRoundupsProfile,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
    initialValues,
    validationSchema,
  })

  const handleSelect = (value: boolean) => {
    if (value !== roundupsEnabled) {
      setFieldValue('roundupsEnabled', value)
      setRoundupsEnabled(value)
    }
  }

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: RoundUpsSetupScreenViewedEvent,
      flow: getFlow(),
    })
  }, [])

  const BODY_TEXT =
    getRemoteString({ key: 'invest.onboarding.round_ups.automatic_vs_manual.body' }) ??
    HeroText.text
  const CTA_TEXT =
    getRemoteString({ key: 'invest.onboarding.round_ups_interstitial.cta.next' }) ??
    HeroText.ctaLabel

  const AUTOMATIC_TITLE =
    getRemoteString({
      key: 'invest.onboarding.round_ups.automatic_vs_manual.input.automatic.title',
    }) ?? 'Automatic'

  const MANUAL_TITLE =
    getRemoteString({
      key: 'invest.onboarding.round_ups.automatic_vs_manual.input.manual.title',
    }) ?? 'Manual'

  const AUTOMATIC_TEXT =
    getRemoteString({
      key: 'invest.onboarding.round_ups.automatic_vs_manual.input.automatic.detail',
    }) ?? 'We’ll invest the change from all purchases for you'

  const MANUAL_TEXT =
    getRemoteString({
      key: 'invest.onboarding.round_ups.automatic_vs_manual.input.manual.detail',
    }) ?? "You'll need to choose which purchases create Round-Ups® each time"

  return (
    <>
      <Nav back={isDynamicFunnelEnabled ? isBackEnabled(PageName.RoundUpsSetUp) : true} />

      <StyledLayout>
        <Container>
          <StyledBody>{BODY_TEXT}</StyledBody>
          <StyledFormContainer>
            <FormRow>
              <RoundUpSelectionList
                testId={TestIDs.ROUNDUPS_SETUP.AUTOMATIC_OPTION}
                name="automatic"
                checked={roundupsEnabled}
                onClick={() => handleSelect(true)}
                title={AUTOMATIC_TITLE}
                text={AUTOMATIC_TEXT}
              />

              <RoundUpSelectionList
                testId={TestIDs.ROUNDUPS_SETUP.MANUAL_OPTION}
                name="manual"
                checked={!roundupsEnabled}
                onClick={() => handleSelect(false)}
                title={MANUAL_TITLE}
                text={MANUAL_TEXT}
              />
            </FormRow>
          </StyledFormContainer>
          <ActionWrapper>
            <Separator />
            <StyledButton data-testid={TestIDs.ROUNDUPS_SETUP.NEXT_BUTTON} onPress={handleSubmit}>
              {CTA_TEXT}
            </StyledButton>
          </ActionWrapper>
        </Container>
      </StyledLayout>
    </>
  )
}

export default enhance(RoundupsSetup)
